// Roadmap Service for managing roadmap data with Firebase Firestore
import { db } from '../../firebase';
import {
    collection,
    doc,
    setDoc,
    getDoc,
    getDocs,
    updateDoc,
    query,
    where,
    orderBy,
    serverTimestamp
} from 'firebase/firestore';

/**
 * Save roadmap data to Firestore
 * @param {Object} roadmapData - Complete roadmap data
 * @returns {Promise<string>} - Roadmap ID
 */
export const saveRoadmapData = async (roadmapData) => {
    try {
        const roadmapId = roadmapData.roadmapId || `RDM${Date.now()}`;
        const roadmapRef = doc(db, 'roadmaps', roadmapId);

        const dataToSave = {
            ...roadmapData,
            roadmapId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            status: 'submitted'
        };

        await setDoc(roadmapRef, dataToSave);
        console.log('Roadmap data saved successfully:', roadmapId);
        return roadmapId;
    } catch (error) {
        console.error('Error saving roadmap data:', error);
        throw error;
    }
};

/**
 * Get roadmap data by ID
 * @param {string} roadmapId - Roadmap ID
 * @returns {Promise<Object>} - Roadmap data
 */
export const getRoadmapById = async (roadmapId) => {
    try {
        const roadmapRef = doc(db, 'roadmaps', roadmapId);
        const roadmapSnap = await getDoc(roadmapRef);

        if (roadmapSnap.exists()) {
            return {
                id: roadmapSnap.id,
                ...roadmapSnap.data(),
                createdAt: roadmapSnap.data().createdAt?.toDate(),
                updatedAt: roadmapSnap.data().updatedAt?.toDate()
            };
        } else {
            return null;
        }
    } catch (error) {
        console.error('Error fetching roadmap:', error);
        throw error;
    }
};

/**
 * Get roadmap data by Report ID
 * @param {string} reportId - Diagnosis Report ID
 * @returns {Promise<Object>} - Roadmap data
 */
export const getRoadmapByReportId = async (reportId) => {
    try {
        const roadmapsRef = collection(db, 'roadmaps');
        const q = query(roadmapsRef, where('reportId', '==', reportId));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const docSnap = querySnapshot.docs[0];
            return {
                id: docSnap.id,
                ...docSnap.data(),
                createdAt: docSnap.data().createdAt?.toDate(),
                updatedAt: docSnap.data().updatedAt?.toDate()
            };
        }
        return null;
    } catch (error) {
        console.error('Error fetching roadmap by reportId:', error);
        throw error;
    }
};

/**
 * Get Roadmap Module Settings
 * @returns {Promise<Object>} - Settings object
 */
export const getRoadmapSettings = async () => {
    try {
        const settingsRef = doc(db, 'appSettings', 'roadmap');
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) {
            return settingsSnap.data();
        }
        return { enabled: true }; // Default enabled
    } catch (error) {
        console.error('Error fetching roadmap settings:', error);
        return { enabled: true };
    }
};

/**
 * Update Roadmap Module Settings
 * @param {boolean} enabled - Toggle status
 * @returns {Promise<void>}
 */
export const updateRoadmapSettings = async (enabled) => {
    try {
        const settingsRef = doc(db, 'appSettings', 'roadmap');
        await setDoc(settingsRef, { 
            enabled, 
            updatedAt: serverTimestamp() 
        }, { merge: true });
        console.log('Roadmap settings updated:', enabled);
    } catch (error) {
        console.error('Error updating roadmap settings:', error);
        throw error;
    }
};

/**
 * Get Income Type Settings
 * @returns {Promise<Object>} - Settings object
 */
export const getIncomeTypeSettings = async () => {
    try {
        const settingsRef = doc(db, 'appSettings', 'incomeTypes');
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) {
            return settingsSnap.data();
        }
        return { partnershipEnabled: true }; // Default enabled
    } catch (error) {
        console.error('Error fetching income type settings:', error);
        return { partnershipEnabled: true };
    }
};

/**
 * Update Income Type Settings
 * @param {boolean} partnershipEnabled - Toggle status
 * @returns {Promise<void>}
 */
export const updateIncomeTypeSettings = async (partnershipEnabled) => {
    try {
        const settingsRef = doc(db, 'appSettings', 'incomeTypes');
        await setDoc(settingsRef, { 
            partnershipEnabled, 
            updatedAt: serverTimestamp() 
        }, { merge: true });
        console.log('Income Type settings updated:', partnershipEnabled);
    } catch (error) {
        console.error('Error updating income type settings:', error);
        throw error;
    }
};

/**
 * Get Couple Income Type Setting
 * @returns {Promise<Object>} - Settings object
 */
export const getCoupleSettings = async () => {
    try {
        const settingsRef = doc(db, 'appSettings', 'incomeTypes');
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) {
            const data = settingsSnap.data();
            return { coupleEnabled: data.coupleEnabled !== false }; // default true
        }
        return { coupleEnabled: true };
    } catch (error) {
        console.error('Error fetching couple settings:', error);
        return { coupleEnabled: true };
    }
};

/**
 * Update Couple Income Type Setting
 * @param {boolean} coupleEnabled - Toggle status
 * @returns {Promise<void>}
 */
export const updateCoupleSettings = async (coupleEnabled) => {
    try {
        const settingsRef = doc(db, 'appSettings', 'incomeTypes');
        await setDoc(settingsRef, {
            coupleEnabled,
            updatedAt: serverTimestamp()
        }, { merge: true });
        console.log('Couple setting updated:', coupleEnabled);
    } catch (error) {
        console.error('Error updating couple settings:', error);
        throw error;
    }
};

export default {
    saveRoadmapData,
    getRoadmapById,
    getRoadmapByReportId,
    getRoadmapSettings,
    updateRoadmapSettings,
    getIncomeTypeSettings,
    updateIncomeTypeSettings,
    getCoupleSettings,
    updateCoupleSettings
};
